import { useState } from "react";
import { createPortal } from "react-dom";
import { Share2, Copy, X, Facebook, Linkedin, Mail, MessageCircle, Twitter, Ticket, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Reusable Share-Event button with a popup of social share options.
 * 
 * Props:
 *  - event:       { event_id, title, description, date, location, image_url, media? }
 *  - origin:      optional override (defaults to window.location.origin)
 *  - size:        "sm" | "default" | "lg"  (default "sm")
 *  - variant:     "icon" | "button"        (default "button" — icon shows only the share icon)
 *  - includeTicketLink:  whether to share both event-details + ticket-purchase URLs (default true)
 *  - stopPropagation:    set true when used inside a Link-wrapped card
 */
export default function ShareEventButton({
  event,
  origin,
  size = "sm",
  variant = "button",
  includeTicketLink = true,
  stopPropagation = false,
  className = "",
  "data-testid": dataTestId,
}) {
  const [open, setOpen] = useState(false);
  if (!event) return null;

  const base = (origin || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const eventUrl = `${base}/events/${event.event_id}`;
  const ticketUrl = `${base}/events/${event.event_id}#tickets`;

  const title = event.title || "Blue vs Blue Event";
  const dateStr = event.date ? new Date(event.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
  const loc = event.location ? ` at ${event.location}` : "";
  const summary = `${title}${dateStr ? ` — ${dateStr}` : ""}${loc}\n\nJoin us! Get your tickets:`;
  const shareText = `${summary}\n${ticketUrl}\n\nEvent details: ${eventUrl}`;
  const shortText = `${title}${dateStr ? ` — ${dateStr}` : ""}`;

  const handleClick = (e) => {
    if (stopPropagation) { e.preventDefault(); e.stopPropagation(); }
    setOpen(!open);
  };

  const closeOn = (e) => { if (stopPropagation) { e.preventDefault(); e.stopPropagation(); } setOpen(false); };

  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ticketUrl)}&quote=${encodeURIComponent(shortText)}`;
  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const ln = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ticketUrl)}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const sms = `sms:?&body=${encodeURIComponent(shareText)}`;
  const mailto = `mailto:?subject=${encodeURIComponent(`You're invited: ${title}`)}&body=${encodeURIComponent(shareText)}`;

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Clipboard unavailable — long-press to copy"); }
    setOpen(false);
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: summary, url: ticketUrl });
        setOpen(false);
      } catch { /* user cancelled */ }
    } else {
      copy(shareText, "Share text");
    }
  };

  return (
    <div className={`inline-block ${className}`} onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}>
      <Button
        type="button"
        size={size}
        variant="outline"
        onClick={handleClick}
        className="gap-1.5"
        data-testid={dataTestId || `share-event-${event.event_id}`}
      >
        <Share2 className="w-4 h-4" />
        {variant === "button" && <span>Share</span>}
      </Button>

      {open && typeof document !== "undefined" && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/40"
            onClick={closeOn}
            data-testid="share-popup-backdrop"
          />
          <div
            className="
              fixed z-[9999] bg-white shadow-2xl
              inset-x-0 bottom-0 w-full rounded-t-2xl
              sm:left-auto sm:right-4 sm:top-20 sm:bottom-auto sm:w-80 sm:rounded-lg sm:border sm:border-slate-200
              max-h-[85vh] overflow-y-auto
            "
            data-testid="share-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sm:hidden flex justify-center py-2">
              <div className="w-10 h-1 bg-slate-300 rounded-full"></div>
            </div>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Share Event</p>
                <p className="text-sm font-medium text-deep-blue truncate" title={title}>{title}</p>
              </div>
              <button onClick={closeOn} className="text-slate-400 hover:text-slate-600" aria-label="Close" data-testid="share-popup-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-1 p-2">
              <a href={fb} target="_blank" rel="noreferrer" onClick={closeOn} className="flex flex-col items-center gap-1 p-2 rounded hover:bg-blue-50" data-testid="share-facebook">
                <Facebook className="w-5 h-5 text-[#1877F2]" />
                <span className="text-[10px] text-slate-600">Facebook</span>
              </a>
              <a href={tw} target="_blank" rel="noreferrer" onClick={closeOn} className="flex flex-col items-center gap-1 p-2 rounded hover:bg-slate-50" data-testid="share-twitter">
                <Twitter className="w-5 h-5 text-slate-900" />
                <span className="text-[10px] text-slate-600">X / Twitter</span>
              </a>
              <a href={ln} target="_blank" rel="noreferrer" onClick={closeOn} className="flex flex-col items-center gap-1 p-2 rounded hover:bg-blue-50" data-testid="share-linkedin">
                <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                <span className="text-[10px] text-slate-600">LinkedIn</span>
              </a>
              <a href={wa} target="_blank" rel="noreferrer" onClick={closeOn} className="flex flex-col items-center gap-1 p-2 rounded hover:bg-green-50" data-testid="share-whatsapp">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                <span className="text-[10px] text-slate-600">WhatsApp</span>
              </a>
              <a href={sms} onClick={closeOn} className="flex flex-col items-center gap-1 p-2 rounded hover:bg-slate-50" data-testid="share-sms">
                <MessageCircle className="w-5 h-5 text-deep-blue" />
                <span className="text-[10px] text-slate-600">Text / SMS</span>
              </a>
              <a href={mailto} onClick={closeOn} className="flex flex-col items-center gap-1 p-2 rounded hover:bg-slate-50" data-testid="share-email">
                <Mail className="w-5 h-5 text-deep-blue" />
                <span className="text-[10px] text-slate-600">Email</span>
              </a>
            </div>

            <div className="border-t border-slate-100 px-2 py-2 space-y-1">
              <button
                type="button"
                onClick={() => copy(ticketUrl, "Ticket link")}
                className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-slate-50 text-left text-sm"
                data-testid="share-copy-ticket"
              >
                <Ticket className="w-4 h-4 text-deep-blue shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-deep-blue">Copy ticket link</div>
                  <div className="text-[10px] text-slate-500 truncate font-mono">{ticketUrl}</div>
                </div>
                <Copy className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </button>
              <button
                type="button"
                onClick={() => copy(eventUrl, "Event details link")}
                className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-slate-50 text-left text-sm"
                data-testid="share-copy-event"
              >
                <Calendar className="w-4 h-4 text-deep-blue shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-deep-blue">Copy event details link</div>
                  <div className="text-[10px] text-slate-500 truncate font-mono">{eventUrl}</div>
                </div>
                <Copy className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </button>
              {includeTicketLink && (
                <button
                  type="button"
                  onClick={() => copy(shareText, "Full share message")}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-slate-50 text-left text-sm"
                  data-testid="share-copy-message"
                >
                  <Copy className="w-4 h-4 text-deep-blue shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-deep-blue">Copy full share message</div>
                    <div className="text-[10px] text-slate-500">Includes title + date + both links</div>
                  </div>
                </button>
              )}
              {typeof navigator !== "undefined" && navigator.share && (
                <button
                  type="button"
                  onClick={nativeShare}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded bg-deep-blue text-white hover:opacity-90 text-sm"
                  data-testid="share-native"
                >
                  <Share2 className="w-4 h-4 shrink-0" />
                  <span className="text-xs font-medium">More options…</span>
                </button>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
